import { getRouteApi } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import ConfirmModal from '@/components/ConfirmModal'
import LinkText from '@/components/LinkText/LinkText'
import Table, { type Column, type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import TableTopBarActions from '@/components/TableTopBar/TableTopBarActions'
import Tag from '@/components/Tag/Tag'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import { useNumberColumn } from '@/hooks/useColumns'
import { usePagination } from '@/hooks/usePagination'
import useRowSelection from '@/hooks/useRowSelection'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { useSorting } from '@/hooks/useSorting'
import { toast } from '@/hooks/useToast'
import { getColumnWidth } from '@/utils/get-column-width'
import { getValue } from '@/utils/get-value'

import BulkRemoveConfirmModal from '../components/BulkRemoveConfirmModal'
import RoomListFilters from '../components/RoomListFilters'
import { roomTypeLabelAndColorMap } from '../constants/room-type-to-tag-color-map'
import type { RoomType } from '../constants/room-types'
import useBulkRemoveRooms from '../mutations/useBulkRemoveRooms'
import useRemoveRoom from '../mutations/useRemoveRoom'
import { type Room, useRooms } from '../queries/useRooms'
import { useRoomsIds } from '../queries/useRoomsIds'

const routeApi = getRouteApi('/_auth/rooms/')

const RoomsListView = () => {
  const { t } = useTranslation(['rooms', 'common'])
  const { id, page, pageSize, type, sort, order } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const selectedIds = id?.map(getValue) || []

  const params = {
    id: selectedIds,
    type,
    sort,
    order
  }

  const {
    data: rooms,
    isLoading: isRoomsLoading,
    refetch: refetchRooms
  } = useRooms({
    ...params,
    page,
    pageSize
  })

  const {
    data: roomsIds,
    isLoading: isRoomsIdsLoading,
    refetch: refetchRoomsIds
  } = useRoomsIds(params)

  const {
    data: allRoomsIds,
    isLoading: isAllRoomsIdsLoading,
    refetch: refetchAllRoomsIds
  } = useRoomsIds({})

  const refetchRoomsData = () => {
    refetchRooms()
    refetchRoomsIds()
    refetchAllRoomsIds()
  }

  const isLoading = isRoomsLoading || isRoomsIdsLoading || isAllRoomsIdsLoading

  const selection = useRowSelection({
    count: roomsIds?.length || 0,
    itemsToSelect: roomsIds?.map(room => room.id) || []
  })

  const { isExtraLarge } = useScreenResolution()

  const [openBulkRemoveModal, setOpenBulkRemoveModal] = useState(false)
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null)

  const { mutate: removeRoom, isPending: isRemoveRoomPending } = useRemoveRoom({
    onSuccess: () => {
      toast({
        title: t('toast.room-was-removed', {
          ROOM: currentRoom?.name
        }),
        variant: 'success'
      })
      setCurrentRoom(null)
      selection.clearSelection()
      refetchRoomsData()
    },
    onError: () => {
      toast({
        title: t('toast.failed-to-remove-room', {
          ROOM: currentRoom?.name
        }),
        variant: 'error'
      })
    }
  })

  const { mutate: bulkRemoveRooms, isPending: isBulkRemoveRoomsPending } =
    useBulkRemoveRooms({
      onSuccess: ({ successfully_deleted, unsuccessfully_deleted }) => {
        if (successfully_deleted > 0) {
          toast({
            title: t('toast.rooms-were-removed', {
              count: successfully_deleted
            }),
            variant: 'success'
          })
        }
        if (unsuccessfully_deleted > 0) {
          toast({
            title: t('toast.failed-to-remove-rooms', {
              count: unsuccessfully_deleted
            }),
            variant: 'error'
          })
        }
        setOpenBulkRemoveModal(false)
        selection.clearSelection()
        refetchRoomsData()
      },
      onError: () => {
        toast({ title: t('toast.failed-to-remove-room'), variant: 'error' })
      }
    })

  const unableToRemoveRooms = useMemo(
    () =>
      selection.selectedItems
        .map(roomId => roomsIds?.find(room => room.id === roomId))
        .filter(
          (room): room is { id: string; name: string; canBeDeleted: boolean } =>
            !!room && !room.canBeDeleted
        ),
    [roomsIds, selection.selectedItems]
  )

  const tableColumns: Column<Room>[] = [
    useNumberColumn(),
    {
      key: 'name',
      title: t('header.name'),
      width: TABLE_CELL_WIDTH.LG,
      render: (value: Room) => (
        <LinkText
          to="/rooms/$roomId"
          params={{
            roomId: value.id
          }}
        >
          {value.name}
        </LinkText>
      )
    },
    {
      dataIndex: 'capacity',
      key: 'capacity',
      title: t('header.capacity'),
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      sorterKey: 'capacity'
    },
    {
      dataIndex: 'roomType',
      key: 'type',
      title: t('header.type'),
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      render: (value: RoomType) => (
        <Tag
          variant="with-background"
          label={t(`room-type.${value}`)}
          color={roomTypeLabelAndColorMap[value]}
        />
      )
    }
  ]

  const actions: TableAction<Room>[] = [
    {
      icon: <EditIcon />,
      text: t('button.edit'),
      onClick: ({ id: roomId }) => {
        navigate({
          to: '/rooms/$roomId/edit',
          params: { roomId }
        })
      }
    },
    {
      icon: <DeleteIcon />,
      text: t('button.remove'),
      variantAction: 'danger',
      onClick: row => {
        if (!row.canBeDeleted) {
          toast({
            title: t('toast.cannot-remove-room', {
              ROOM: row.name
            }),
            description: t('toast.room-linked-to-active-lesson'),
            variant: 'error'
          })
          return
        }
        setCurrentRoom(row)
      }
    }
  ]

  const bulkActions = [
    {
      text: t('button.remove'),
      icon: <DeleteIcon />,
      onClick: () => setOpenBulkRemoveModal(true)
    }
  ]

  const handleOpenChange = (open: boolean) => {
    if (!open) setCurrentRoom(null)
  }

  const pagination = usePagination(routeApi)
  const sorting = useSorting(routeApi)

  const selectedItemsCount = selection.selectedItems.length

  return (
    <BasicLayout
      header={t('header.rooms')}
      moduleName={t('header.administration', { ns: 'common' })}
      breadcrumbs={[{ title: t('common:navigation.rooms') }]}
    >
      <RoomListFilters />
      <Table
        id="rooms-table"
        isLoading={isLoading}
        data={rooms?.list || []}
        actions={actions}
        columns={tableColumns}
        count={rooms?.count}
        totalCount={allRoomsIds?.length}
        {...pagination}
        {...sorting}
        rowSelection={{
          selectedItems: selection.selectedItems,
          allCheckboxValue: selection.getSelectAllCheckboxValue(),
          count: rooms?.count || 0,
          labelHeader: selected =>
            selected
              ? t('label.unselect-all-rooms')
              : t('label.select-all-rooms'),
          labelItem: ({ name, selected }) =>
            selected
              ? t('label.unselect-room', { ROOM: name })
              : t('label.select-room', { ROOM: name }),
          onSelect: selection.onSelect,
          onSelectAll: selection.onSelectAll
        }}
        rightTopBar={
          <TableTopBarActions
            actions={bulkActions}
            selectedItemsLength={selectedItemsCount}
          />
        }
        caption={t('header.rooms')}
      />
      <ConfirmModal
        header={t('header.remove-rooms', { count: selectedItemsCount })}
        subheader={
          <>
            <p>{t('help.want-to-remove-room', { NAME: currentRoom?.name })}</p>
            <p>{t('help.this-action-cannot-be-undone')}</p>
          </>
        }
        confirmButton={
          <Button
            variant="danger"
            onClick={() =>
              currentRoom && removeRoom({ roomId: currentRoom.id })
            }
            loading={isRemoveRoomPending}
          >
            {t('button.remove')}
          </Button>
        }
        open={!!currentRoom}
        onOpenChange={handleOpenChange}
      />
      <BulkRemoveConfirmModal
        open={openBulkRemoveModal}
        onOpenChange={setOpenBulkRemoveModal}
        count={selectedItemsCount}
        isRemoveLoading={isBulkRemoveRoomsPending}
        unableToRemoveRooms={unableToRemoveRooms}
        onRemove={() => bulkRemoveRooms({ ids: selection.selectedItems })}
      />
    </BasicLayout>
  )
}

export default RoomsListView
